import type { AuthTokens, JwtClaims } from '@/lib/api/types';

/**
 * In-memory session store (plan §4.2 / §17 security).
 *
 * The access token lives ONLY here — never localStorage/sessionStorage. The refresh token
 * lives in an httpOnly cookie set by the backend; if the backend instead returns it in the
 * login/refresh body, we keep it in this module variable (still never persisted to web
 * storage). On hard reload the cookie-based refresh re-establishes the session.
 */
interface SessionState {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null; // epoch ms
  claims: JwtClaims | null;
}

const state: SessionState = {
  accessToken: null,
  refreshToken: null,
  expiresAt: null,
  claims: null,
};

type Listener = () => void;
const listeners = new Set<Listener>();
function emit() {
  for (const l of listeners) l();
}

export function subscribeSession(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** Decode a JWT payload without verifying the signature (server is authoritative). */
export function decodeJwt(token: string): JwtClaims | null {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as JwtClaims;
  } catch {
    return null;
  }
}

export function setTokens(tokens: AuthTokens): void {
  state.accessToken = tokens.access_token;
  state.refreshToken = tokens.refresh_token ?? state.refreshToken;
  state.expiresAt = new Date(tokens.expires_at).getTime();
  state.claims = decodeJwt(tokens.access_token);
  emit();
}

export function clearSession(): void {
  state.accessToken = null;
  state.refreshToken = null;
  state.expiresAt = null;
  state.claims = null;
  emit();
}

export function getAccessToken(): string | null {
  return state.accessToken;
}

export function getRefreshToken(): string | null {
  return state.refreshToken;
}

export function getClaims(): JwtClaims | null {
  return state.claims;
}

export function isAuthenticated(): boolean {
  return state.accessToken !== null;
}

/** Milliseconds until the access token expires (negative if already expired). */
export function timeUntilExpiry(): number {
  if (state.expiresAt == null) return Infinity;
  return state.expiresAt - Date.now();
}

export function getSessionSnapshot(): Readonly<SessionState> {
  return state;
}
